import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiPost, apiPut } from "../../lib/api";
import { useTenantAuth } from "../../context/auth";
import { cn } from "../../lib/cn";

type Integration = {
  id: string;
  branch_id: string | null;
  name: string;
  channel: string;
  enabled: boolean;
  config: Record<string, unknown>;
};

type MeOut = {
  user_id: string;
  role: string;
  tenant_id: string;
  telegram_chat_id: string | null;
};

type TelegramBookingTemplateOut = {
  template: string;
  default_template: string;
  placeholders: string[];
};

type IntegrationHealthOut = {
  status: string;
  last_healthcheck_at?: string | null;
  last_health_error?: string | null;
};

function telegramMode(row: Integration | null | undefined): string {
  return String(row?.config?.mode ?? "").toLowerCase();
}

function isSharedTelegram(row: Integration | null | undefined): boolean {
  return telegramMode(row) === "shared_platform_bot";
}

function hasExplicitTelegramToken(row: Integration | null | undefined): boolean {
  const token = row?.config?.bot_token;
  return typeof token === "string" && token.trim().length > 0;
}

function telegramPreferenceRank(row: Integration): number {
  if (row.channel !== "telegram") return 99;
  const shared = isSharedTelegram(row);
  const explicit = hasExplicitTelegramToken(row);
  if (!shared && explicit) return 0;
  if (!shared) return 1;
  if (shared && explicit) return 2;
  return 3;
}

export function TgIntegrationsPage() {
  const qc = useQueryClient();
  const { bootstrap } = useTenantAuth();
  const role = bootstrap?.user.role || "";
  const canAdminIntegrations = role === "owner" || role === "admin";

  const [selectedId, setSelectedId] = useState("");
  const [chatIdInput, setChatIdInput] = useState("");
  const [botTokenInput, setBotTokenInput] = useState("");
  const [integrationNameInput, setIntegrationNameInput] = useState("Main Telegram Bot");
  const [integrationBranchId, setIntegrationBranchId] = useState<string>("");
  const [integrationEnabled, setIntegrationEnabled] = useState(true);
  const [testChatId, setTestChatId] = useState("");
  const [testMessage, setTestMessage] = useState("Test message from SalonOS");
  const [bookingTemplate, setBookingTemplate] = useState("");
  const [bookingTemplateDefault, setBookingTemplateDefault] = useState("");
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const [healthResult, setHealthResult] = useState<IntegrationHealthOut | null>(null);
  const [testResult, setTestResult] = useState("");

  const list = useQuery({
    queryKey: ["tg-integrations-list"],
    queryFn: () => apiGet<{ items: Integration[] }>("/v3/integrations?size=200"),
  });
  const me = useQuery({
    queryKey: ["tg-integrations-me"],
    queryFn: () => apiGet<MeOut>("/v3/auth/me"),
  });

  const items = list.data?.items ?? [];
  const telegramItems = useMemo(
    () =>
      items
        .filter((i) => i.channel === "telegram")
        .slice()
        .sort((a, b) => telegramPreferenceRank(a) - telegramPreferenceRank(b)),
    [items]
  );
  const selected = items.find((i) => i.id === selectedId) ?? null;
  const selectedTelegram = selected?.channel === "telegram" ? selected : null;
  const activeTelegram = selectedTelegram ?? telegramItems[0] ?? null;
  const selectedTelegramId = activeTelegram?.id ?? "";
  const activeTelegramIsShared = isSharedTelegram(activeTelegram);
  const activeTelegramHasExplicitToken = hasExplicitTelegramToken(activeTelegram);
  const webhookSyncBlockedForShared = activeTelegramIsShared && !activeTelegramHasExplicitToken;

  useEffect(() => {
    if (!selectedId && items.length) setSelectedId(items[0].id);
  }, [items, selectedId]);

  useEffect(() => {
    const seed = activeTelegram;
    if (!seed) return;
    setIntegrationNameInput((prev) => prev || seed.name || "Main Telegram Bot");
    setIntegrationBranchId((prev) => prev || seed.branch_id || "");
    setIntegrationEnabled(seed.enabled);
    const token = seed.config?.bot_token;
    if (typeof token === "string" && token && !botTokenInput) {
      setBotTokenInput(token);
    }
  }, [activeTelegram, botTokenInput]);

  const branchNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const b of bootstrap?.branches ?? []) map.set(b.id, b.name);
    return map;
  }, [bootstrap?.branches]);

  const bookingTemplateQuery = useQuery({
    queryKey: ["tg-telegram-booking-template", selectedTelegramId],
    queryFn: () =>
      apiGet<TelegramBookingTemplateOut>(
        `/v3/integrations/${selectedTelegramId}/telegram/templates/customer-booking-confirmation`
      ),
    enabled: Boolean(selectedTelegramId),
  });

  useEffect(() => {
    if (!bookingTemplateQuery.data) return;
    setBookingTemplate(bookingTemplateQuery.data.template || "");
    setBookingTemplateDefault(bookingTemplateQuery.data.default_template || "");
  }, [bookingTemplateQuery.data]);

  const saveChatId = useMutation({
    mutationFn: (chatId: string | null) => apiPatch<MeOut>("/v3/auth/profile", { telegram_chat_id: chatId }),
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: ["tg-integrations-me"] });
      setNotice(`Telegram chat linked: ${data.telegram_chat_id ?? "cleared"}`);
      setError("");
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Failed to save chat id");
      setNotice("");
    },
  });

  const saveTelegramIntegration = useMutation({
    mutationFn: async () => {
      if (!canAdminIntegrations) throw new Error("Only owner/admin can create or update integrations.");
      const token = botTokenInput.trim();
      if (!token) throw new Error("Enter Telegram bot token.");
      const validate = await apiPost<{ ok: boolean; details: Record<string, unknown> }>(
        "/v3/integrations/telegram/validate-token",
        { bot_token: token }
      );
      if (!validate.ok) {
        throw new Error(`Token validation failed: ${JSON.stringify(validate.details)}`);
      }
      const target = selectedTelegram ?? telegramItems[0] ?? null;
      const payload = {
        branch_id: integrationBranchId || null,
        channel: "telegram",
        name: integrationNameInput.trim() || target?.name || "Main Telegram Bot",
        config: { ...(target?.config ?? {}), mode: "custom_bot", bot_token: token },
        enabled: integrationEnabled,
      };
      if (target) {
        return apiPut<Integration>(`/v3/integrations/${target.id}`, payload);
      }
      return apiPost<Integration>("/v3/integrations", payload);
    },
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: ["tg-integrations-list"] });
      setSelectedId(data.id);
      setNotice(`Telegram integration saved: ${data.name}`);
      setError("");
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Failed to save integration");
      setNotice("");
    },
  });

  const syncWebhook = useMutation({
    mutationFn: () => {
      if (!selectedTelegramId) throw new Error("Select a Telegram integration first.");
      if (!canAdminIntegrations) throw new Error("Only owner/admin can sync webhook.");
      return apiPost<{ ok: boolean; webhook_url: string }>(
        `/v3/integrations/${selectedTelegramId}/telegram/webhook/sync`,
        {}
      );
    },
    onSuccess: (data) => {
      setNotice(`Webhook sync ${data.ok ? "ok" : "failed"}: ${data.webhook_url}`);
      setError("");
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Webhook sync failed");
      setNotice("");
    },
  });

  const refreshHealth = useMutation({
    mutationFn: () => {
      if (!selectedId) throw new Error("Select integration first.");
      if (!canAdminIntegrations) throw new Error("Only owner/admin can check health.");
      return apiGet<IntegrationHealthOut>(`/v3/integrations/${selectedId}/health`);
    },
    onSuccess: (data) => {
      setHealthResult(data);
      setNotice(`Health status: ${data.status}`);
      setError("");
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Health check failed");
      setNotice("");
    },
  });

  const testSend = useMutation({
    mutationFn: () => {
      if (!selectedTelegramId) throw new Error("Select a Telegram integration first.");
      return apiPost<{ ok: boolean; chat_id: string }>(`/v3/integrations/${selectedTelegramId}/telegram/test-send`, {
        chat_id: testChatId,
        message: testMessage,
      });
    },
    onSuccess: (data) => {
      setTestResult(JSON.stringify(data, null, 2));
      setNotice("Test message request sent.");
      setError("");
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : "Test send failed";
      setTestResult(msg);
      setError(msg);
      setNotice("");
    },
  });

  const saveTemplate = useMutation({
    mutationFn: () => {
      if (!selectedTelegramId) throw new Error("Select a Telegram integration first.");
      return apiPatch<TelegramBookingTemplateOut>(
        `/v3/integrations/${selectedTelegramId}/telegram/templates/customer-booking-confirmation`,
        { template: bookingTemplate }
      );
    },
    onSuccess: async (data) => {
      await qc.invalidateQueries({ queryKey: ["tg-telegram-booking-template", selectedTelegramId] });
      setBookingTemplate(data.template || "");
      setBookingTemplateDefault(data.default_template || "");
      setNotice("Booking template saved.");
      setError("");
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Template save failed");
      setNotice("");
    },
  });

  const currentChatId = me.data?.telegram_chat_id || "";

  return (
    <div className="space-y-4 p-4 pb-24">
      <div className="rounded-2xl bg-slate-900 p-4 text-white">
        <p className="text-xs uppercase tracking-wide text-slate-300">Integrations</p>
        <h1 className="mt-1 text-lg font-semibold">Telegram-first Controls</h1>
        <p className="mt-1 text-xs text-slate-300">
          Shared platform bot is active by default for sales-assisted onboarding. Upgrade to your own bot later if you need branding.
        </p>
      </div>

      {notice && <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2 text-sm text-emerald-700">{notice}</div>}
      {error && <div className="rounded-lg border border-rose-200 bg-rose-50 p-2 text-sm text-rose-700">{error}</div>}

      <section className="rounded-xl border border-gray-200 bg-white p-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900">Connected channels</h2>
          <span className="text-xs text-gray-500">{items.length}</span>
        </div>
        <div className="mt-2 space-y-2">
          {items.map((row) => (
            <button
              key={row.id}
              onClick={() => setSelectedId(row.id)}
              className={cn(
                "w-full rounded-xl border p-3 text-left",
                selectedId === row.id ? "border-teal-500 bg-teal-50" : "border-gray-200 bg-white"
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-gray-900">{row.name}</p>
                  <p className="text-xs text-gray-500">
                    {row.channel} · {row.branch_id ? branchNameMap.get(row.branch_id) || "branch" : "all branches"}
                  </p>
                </div>
                <span
                  className={cn(
                    "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                    row.enabled ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-600"
                  )}
                >
                  {row.enabled ? "enabled" : "disabled"}
                </span>
              </div>
            </button>
          ))}
          {!list.isLoading && items.length === 0 && (
            <p className="rounded-lg border border-dashed border-gray-300 p-3 text-sm text-gray-500">
              No integrations yet.
            </p>
          )}
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900">Manager Telegram link</h2>
          {currentChatId ? <span className="text-xs text-emerald-700">linked</span> : <span className="text-xs text-gray-500">not linked</span>}
        </div>
        {currentChatId ? (
          <p className="text-xs text-gray-600">
            Current chat id: <code>{currentChatId}</code>
          </p>
        ) : (
          <p className="text-xs text-gray-600">
            Link your Telegram chat to receive manager actions in bot chats.
          </p>
        )}
        <div className="grid gap-2">
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={chatIdInput}
            placeholder="Telegram chat id"
            onChange={(e) => setChatIdInput(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              onClick={() => saveChatId.mutate(chatIdInput.trim() || null)}
              disabled={saveChatId.isPending}
            >
              {saveChatId.isPending ? "Saving..." : "Save link"}
            </button>
            <button
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
              onClick={() => {
                setChatIdInput("");
                saveChatId.mutate(null);
              }}
              disabled={saveChatId.isPending}
            >
              Unlink
            </button>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900">Telegram bot setup</h2>
          <span className="text-xs text-gray-500">{canAdminIntegrations ? role : `${role} (view/test only)`}</span>
        </div>
        {activeTelegramIsShared ? (
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-2 text-xs text-sky-900">
            Shared platform bot is currently active for this tenant. You can finish onboarding and work immediately. Paste a bot token below only when you want to upgrade to your own branded bot.
          </div>
        ) : null}
        <p className="text-xs text-gray-600">Create or update Telegram integration. Uses token validation before save.</p>
        <div className="grid gap-2">
          <select
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={integrationBranchId}
            onChange={(e) => setIntegrationBranchId(e.target.value)}
          >
            <option value="">All branches</option>
            {(bootstrap?.branches ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={integrationNameInput}
            placeholder="Integration name"
            onChange={(e) => setIntegrationNameInput(e.target.value)}
          />
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            type="password"
            value={botTokenInput}
            placeholder="Telegram bot token"
            onChange={(e) => setBotTokenInput(e.target.value)}
          />
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={integrationEnabled}
              onChange={(e) => setIntegrationEnabled(e.target.checked)}
            />
            Integration enabled
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              onClick={() => saveTelegramIntegration.mutate()}
              disabled={!botTokenInput.trim() || saveTelegramIntegration.isPending}
            >
              {saveTelegramIntegration.isPending
                ? "Saving..."
                : activeTelegramIsShared
                  ? "Upgrade to own bot"
                  : "Save bot"}
            </button>
            <button
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
              onClick={() => syncWebhook.mutate()}
              disabled={!selectedTelegramId || syncWebhook.isPending || webhookSyncBlockedForShared}
              title={webhookSyncBlockedForShared ? "Shared platform bot uses platform-managed routing. Upgrade to your own bot to sync a tenant webhook." : undefined}
            >
              {syncWebhook.isPending ? "Syncing..." : "Sync webhook"}
            </button>
          </div>
          {webhookSyncBlockedForShared ? (
            <p className="text-xs text-amber-700">
              Webhook sync is disabled for shared platform bot mode. Upgrade to your own bot when you need dedicated inbound commands and tenant webhook routing.
            </p>
          ) : null}
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-900">Selected integration</h2>
          <button
            className="text-xs font-semibold text-teal-600 disabled:opacity-50"
            onClick={() => refreshHealth.mutate()}
            disabled={!selectedId || refreshHealth.isPending}
          >
            {refreshHealth.isPending ? "Checking..." : "Refresh health"}
          </button>
        </div>
        {selected ? (
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-2 text-xs text-gray-700">
            <div className="grid grid-cols-2 gap-x-3 gap-y-1">
              <span>Name</span>
              <span className="font-semibold">{selected.name}</span>
              <span>Channel</span>
              <span>{selected.channel}</span>
              <span>Enabled</span>
              <span>{selected.enabled ? "yes" : "no"}</span>
              <span>Branch</span>
              <span>{selected.branch_id ? branchNameMap.get(selected.branch_id) || selected.branch_id : "all"}</span>
              {selected.channel === "telegram" ? (
                <>
                  <span>Mode</span>
                  <span>{isSharedTelegram(selected) ? "shared_platform_bot" : "custom_bot"}</span>
                </>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="text-xs text-gray-500">Select an integration.</p>
        )}
        {healthResult && (
          <div className="rounded-lg border border-gray-100 bg-gray-50 p-2 text-xs">
            <p>
              Status: <strong>{healthResult.status}</strong>
            </p>
            {healthResult.last_health_error ? <p className="text-rose-700">Error: {healthResult.last_health_error}</p> : null}
            {healthResult.last_healthcheck_at ? <p className="text-gray-500">Checked: {new Date(healthResult.last_healthcheck_at).toLocaleString()}</p> : null}
          </div>
        )}
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
        <h2 className="text-sm font-semibold text-gray-900">Telegram test send</h2>
        <p className="text-xs text-gray-600">Sends a test message via selected Telegram integration.</p>
        <div className="grid gap-2">
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={testChatId}
            placeholder={currentChatId ? `Chat id (or use mine: ${currentChatId})` : "Recipient chat id"}
            onChange={(e) => setTestChatId(e.target.value)}
          />
          <input
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={testMessage}
            placeholder="Message text"
            onChange={(e) => setTestMessage(e.target.value)}
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
              onClick={() => setTestChatId(currentChatId)}
              disabled={!currentChatId}
            >
              Use my chat id
            </button>
            <button
              className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
              onClick={() => testSend.mutate()}
              disabled={!selectedTelegramId || !testChatId.trim() || !testMessage.trim() || testSend.isPending}
            >
              {testSend.isPending ? "Sending..." : "Send test"}
            </button>
          </div>
          <pre className="max-h-36 overflow-auto rounded-lg bg-slate-100 p-2 text-xs text-slate-700">
            {testResult || "No result yet"}
          </pre>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-3 space-y-2">
        <h2 className="text-sm font-semibold text-gray-900">Booking confirmation template</h2>
        <p className="text-xs text-gray-600">
          {selectedTelegramId
            ? `Placeholders: ${(bookingTemplateQuery.data?.placeholders ?? ["customer_name", "service_name", "staff_name", "start_at"])
                .map((x) => `{{${x}}}`)
                .join(", ")}`
            : "Select a Telegram integration first."}
        </p>
        <textarea
          className="min-h-[160px] w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
          value={bookingTemplate}
          onChange={(e) => setBookingTemplate(e.target.value)}
          disabled={!selectedTelegramId || bookingTemplateQuery.isLoading}
        />
        <div className="grid grid-cols-2 gap-2">
          <button
            className="rounded-lg bg-teal-600 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
            onClick={() => saveTemplate.mutate()}
            disabled={!selectedTelegramId || !bookingTemplate.trim() || saveTemplate.isPending}
          >
            {saveTemplate.isPending ? "Saving..." : "Save template"}
          </button>
          <button
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:opacity-50"
            onClick={() => setBookingTemplate(bookingTemplateDefault)}
            disabled={!bookingTemplateDefault}
          >
            Reset default
          </button>
        </div>
      </section>
    </div>
  );
}
